/**
 * The pieces a call is read with, shared by the two places one is read:
 * the arrival page (#85), where a call about no job is opened on its
 * own, and the job page (#115), where every call about one job is
 * drawn in one thread. One set of parts, so a call cannot read one way
 * on its own and another under its job.
 *
 * The record's rows, the answers, the transcript as turns, the
 * recording and the player are all here; what each page owns is the
 * order it puts them in and what it says on top.
 */
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InlineBanner } from "../ui/InlineBanner";
import { PauseIcon, PlayIcon, RotateCcwIcon } from "../ui/icons";
import {
  editCallArrival,
  type TranscriptTurn,
} from "../../lib/arrivals-api";
import {
  heldRecordingBars,
  heldRecordingSeconds,
  heldRecordingSrc,
  recordingSrc,
  recordingWaveform,
} from "../../lib/recordings";
import { track } from "../../lib/analytics";
import { useSettingCommit } from "../../lib/use-setting-commit";
import { outcomeOf, type OutcomeFacts } from "../../lib/arrivals-format";
import { readTurns, timedTurns, type Turn } from "../../lib/transcript";
import { errorMessage } from "../../lib/error-message";
import { BAR_COUNT } from "../../lib/waveform";
import styles from "./CallParts.module.css";

/** What an edit of one call hands back to the screen that drew it. */
export type CallEdited = (next: { summary?: string; email?: string }) => void;

/**
 * One field of the call under the settings save model: it commits when
 * it is left, Escape puts the stored value back, and a value nobody
 * changed is never written. Enter leaves a one-line field; a paragraph
 * keeps it. A field that must not be blank puts the stored value back
 * instead of writing one — the server refuses it besides, and the
 * screen does not ask.
 */
export function useInlineEdit(
  stored: string,
  save: (next: string) => Promise<void>,
  { blankReverts = false, enterCommits = false } = {},
) {
  const field = useSettingCommit(stored, save);
  // Escape reverts and then leaves the field; the blur that follows
  // must not commit the draft it is reverting.
  const reverting = useRef(false);

  const border = field.error ? styles.rejected : styles.resting;

  const props = {
    value: field.draft,
    disabled: field.saving,
    "aria-invalid": Boolean(field.error) || undefined,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      field.setDraft(e.target.value),
    onFocus: field.onFocus,
    onBlur: () => {
      if (reverting.current) {
        reverting.current = false;
        return;
      }
      if (blankReverts && field.draft.trim() === "") {
        field.resetDraft();
        return;
      }
      void field.commitDraft();
    },
    onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "Enter" && enterCommits) e.currentTarget.blur();
      if (e.key === "Escape") {
        reverting.current = true;
        field.resetDraft();
        e.currentTarget.blur();
      }
    },
  };

  return { props, border, error: field.error, draft: field.draft };
}

/**
 * What was said, in a sentence or two, before the facts: it is what a
 * person opening the call came to find out, and the transcript under
 * the facts is the whole of it. The one text a call has (#119) — the
 * list and the thread fold it, and a phone Inquiry's paragraph was
 * written from it. The model's reading is a starting point: a sentence
 * that is wrong is fixed here, in place, and what the Company wrote is
 * never written over. Drawn as the paragraph it is,
 * bordered only while it is being edited, and grown to its text so
 * nothing scrolls inside it.
 */
export function CallSummary({
  id,
  summary,
  onEdited,
  className = styles.summaryBlock,
}: {
  id: string;
  summary: string;
  onEdited: CallEdited;
  className?: string;
}) {
  const field = useInlineEdit(summary, async (next) => {
    const saved = await editCallArrival(id, { summary: next });
    onEdited({ summary: saved.summary });
    track("summary_edited");
  });
  const box = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, [field.draft]);

  return (
    <div className={className}>
      <textarea
        ref={box}
        rows={1}
        aria-label="Summary"
        placeholder="What was said, in a sentence or two"
        {...field.props}
        className={`${styles.summary} ${field.border}`}
      />
      {field.error && (
        <InlineBanner variant="error" className={styles.noticeInset}>
          {field.error}
        </InlineBanner>
      )}
    </div>
  );
}

/**
 * The person's email, as a fact that can be typed into. A Receptionist
 * takes one on every call, so it is usually there already; an Operator
 * who answered may not have asked, and this is where it goes when they
 * ring back and do. Blank is allowed — it says there is none.
 */
export function CallEmail({
  id,
  email,
  onEdited,
}: {
  id: string;
  email: string;
  onEdited: CallEdited;
}) {
  const field = useInlineEdit(
    email,
    async (next) => {
      const saved = await editCallArrival(id, { email: next });
      onEdited({ email: saved.email });
    },
    { enterCommits: true },
  );

  return (
    <Fact label="Email">
      <span className={styles.emailCell}>
        <input
          type="text"
          inputMode="email"
          autoComplete="off"
          aria-label="Email"
          {...field.props}
          className={`${styles.email} ${field.border}`}
        />
        {field.error && (
          <InlineBanner variant="error" className={styles.noticeInset}>
            {field.error}
          </InlineBanner>
        )}
      </span>
    </Fact>
  );
}

/**
 * What became of it and what to do about it, as the notes' last row.
 *
 * One row and one sentence, whatever happened. It was two — what it
 * became, then why it was not an inquiry — which said one thing twice
 * and said the halves in the wrong order; and then it was a button to
 * the job, drawn only where the heading had no link to the job, beside
 * a row saying how to get back to them. The link is the heading's, and
 * the sentence here is what happened followed by what to do.
 */
export function Outcome({ facts }: { facts: OutcomeFacts }) {
  const outcome = outcomeOf(facts);
  if (outcome.kind === "failed") {
    return (
      <Fact label="Outcome">
        <span className={styles.failed}>{outcome.text}</span>
      </Fact>
    );
  }
  return <Fact label="Outcome">{outcome.text}</Fact>;
}

export function Fact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{children}</dd>
    </div>
  );
}

/**
 * Where the recording is up to, shared by the player and the Transcript
 * under it (#118).
 *
 * The player reports its position here and takes a seek from here; the
 * Transcript reads the position to say which turn is being heard, and
 * seeks when a turn is pressed. Neither knows the other: the page that
 * draws them both holds this and hands it to each, so a call reads the
 * same way on its own page and under its job.
 */
export interface Playback {
  /** Seconds into the recording. */
  at: number;
  playing: boolean;
  /** The player's, called as it plays, seeks, pauses and ends. */
  onPosition: (at: number, playing: boolean) => void;
  /** Move the recording to this many seconds in, and play from there. */
  seek: (secs: number) => void;
  /** Where the player puts the seek it answers; null with no player. */
  seekRef: MutableRefObject<((secs: number) => void) | null>;
}

export function usePlayback(): Playback {
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(false);
  const seekRef = useRef<((secs: number) => void) | null>(null);
  const onPosition = useCallback((next: number, isPlaying: boolean) => {
    setAt(next);
    setPlaying(isPlaying);
  }, []);
  const seek = useCallback((secs: number) => seekRef.current?.(secs), []);
  return useMemo(
    () => ({ at, playing, onPosition, seek, seekRef }),
    [at, playing, onPosition, seek],
  );
}

/**
 * What was said, as turns where the transcript has them.
 *
 * A record and not a chat: the label alone separates the two sides, and
 * there are no bubbles, no colours and no rules between turns. Forty
 * turns drawn as forty ruled rows is a table of a conversation, which is
 * harder to read than the conversation.
 *
 * Both sides are the same words in the same face, weight and colour:
 * the label alone says who spoke. The Company's side was the quieter
 * grey, on the reading that the half worth reading is the one
 * describing the job — but a transcript is read to find what was said,
 * and a lighter half reads as a weaker one rather than a quieter one.
 * Nothing here depends on the accent colour either, which has its own
 * five jobs (DESIGN.md) and is spent on the turn being heard.
 *
 * A transcript with its timing (#118) lines up with the recording: the
 * turn being heard is the accent's for as long as it runs, its words
 * heavier than the ones around them, which is the selected state and
 * one of the accent's jobs, and every turn is a button that moves the
 * recording to where it starts. Nothing is lit in the silence between
 * one turn's end and the next turn's start. Only while the call is playing is the current turn kept
 * in view, and it is scrolled to rather than jumped to, so a reader
 * following along is carried with the conversation and a reader who
 * has stopped it is never scrolled away from the line they are on. A
 * transcript from before #118 has no timing and is drawn as it always
 * was: nothing is highlighted and nothing is pressed.
 *
 * The caller is named where anybody learned a name: the Inquiry's own
 * contact name, or the name they gave on the call, which the label step
 * reads off this same transcript — so "Caller" survives only for a
 * person who never said who they were, and that is the honest label
 * for one. The Company's own side is never named after the Company: the
 * person reading this works there, and their own name in that column
 * reads as a third party in their own transcript.
 *
 * A transcript with no turns in it — a **Voicemail**, or an engine that
 * could not tell the speakers apart — is drawn as it arrived.
 */
export function Transcript({
  text,
  turns: timed = null,
  caller,
  playback,
}: {
  text: string;
  turns?: TranscriptTurn[] | null;
  caller: string;
  playback?: Playback;
}) {
  const turns = useMemo(() => {
    const own = timedTurns(timed);
    return own.length > 0 ? own : readTurns(text);
  }, [timed, text]);
  // Lined up with the recording only where every turn has a time and
  // there is a recording to line up with.
  const seeks = Boolean(playback) && turns.every((t) => t.startMs !== undefined);
  // The turn being heard: the one whose span holds the position. A turn
  // runs from its start to the end the transcriber gave it, and to the
  // next turn's start where it gave none, which is every turn from
  // before the end was asked for; between one turn's end and the next
  // turn's start nobody is speaking and nothing is lit. Nothing before
  // the first turn starts, and nothing at all until the recording has
  // been played or moved.
  const at = playback ? playback.at * 1000 : 0;
  let current = -1;
  if (seeks && (playback!.playing || playback!.at > 0)) {
    for (let i = 0; i < turns.length; i++) {
      const start = turns[i].startMs ?? 0;
      const end = turns[i].endMs ?? turns[i + 1]?.startMs ?? Infinity;
      if (start <= at && at < end) current = i;
    }
  }

  if (turns.length === 0) {
    return <p className={styles.plainTranscript}>{text}</p>;
  }
  // A turn is a sentence since #167, so one voice's say is a run of
  // them: the label is drawn on the first and the rest sit under it,
  // closer to each other than to the next voice's, because a name over
  // every sentence is a script and not a conversation.
  const named = turns.map((turn) =>
    turn.side === "caller" && caller ? caller : turn.speaker,
  );
  return (
    <div className={styles.turns}>
      {turns.map((turn, i) => (
        <TurnRow
          key={i}
          turn={turn}
          speaker={named[i]}
          first={i === 0 || named[i] !== named[i - 1]}
          current={i === current}
          playing={Boolean(playback?.playing)}
          seek={
            seeks ? () => playback!.seek((turn.startMs ?? 0) / 1000) : undefined
          }
        />
      ))}
    </div>
  );
}

/**
 * One turn. A button where it seeks the recording and a plain row
 * where it does not; the same two lines either way, so an old
 * transcript and a timed one read alike. The speaker is written on the
 * first turn of a voice's run and left blank under it.
 */
function TurnRow({
  turn,
  speaker,
  first,
  current,
  playing,
  seek,
}: {
  turn: Turn;
  speaker: string;
  first: boolean;
  current: boolean;
  playing: boolean;
  seek?: () => void;
}) {
  const row = useRef<HTMLDivElement & HTMLButtonElement>(null);
  // Kept in view while it plays, and only then. `nearest` scrolls
  // nothing for a turn already on screen, so a reader following along
  // is not tugged at on every turn.
  //
  // It scrolls rather than jumps, which is the one place in either app
  // motion is the information: a page that cuts to a new position
  // leaves the reader to work out what moved and how far, and a skip
  // down the recording lands somewhere with nothing to say it came
  // from where they were. Off where the reader has asked for less
  // motion, which is the platform's own setting, not a preference of
  // ours.
  useEffect(() => {
    if (!current || !playing) return;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    row.current?.scrollIntoView?.({
      block: "nearest",
      behavior: still?.matches ? "auto" : "smooth",
    });
  }, [current, playing]);

  // The turn being heard is the accent's, whole and heavier: its words
  // are what a reader is following, and a colour alone at 14px against
  // near-black body text was too close a call to find at a glance.
  const words = `${styles.words} ${current ? styles.heard : ""}`;
  const grid = `${styles.turn} ${first ? styles.firstOfVoice : ""}`;
  // Empty under the first of a voice's run, and still drawn where there
  // is a column for the words to keep.
  const label = `${styles.speaker} ${first ? "" : styles.underRun}`;
  const name = first ? speaker : "";
  if (!seek) {
    return (
      <div ref={row} className={grid}>
        <p className={label}>{name}</p>
        <p className={words}>{turn.text}</p>
      </div>
    );
  }
  // The words carry the state rather than an edge beside them. The
  // scroll margins that keep a turn clear of the pinned player above it,
  // and move the page before the line reaches the fold, are the button's
  // own (`.turnButton`).
  return (
    <button
      ref={row}
      type="button"
      aria-current={current ? "true" : undefined}
      onClick={seek}
      className={`${styles.turnButton} ${grid}`}
    >
      <span className={label}>{name}</span>
      <span className={words}>{turn.text}</span>
    </button>
  );
}

/**
 * The audio, fetched as bytes and played from an object URL.
 *
 * `/api/admin` needs the Authorization header, and a browser fetching an
 * `<audio src>` sends none — so the file cannot be a plain URL on this
 * element, and it must not be one on the public file viewer either, where
 * the storage ref is itself the credential.
 *
 * Read once and held (`lib/recordings`): a player drawn for a call whose
 * audio is already in hand has it, its bars and its length on its first
 * render, and one drawn before that has the bars the moment the bytes
 * are decoded, because the waveform is read from the same bytes and the
 * file is never fetched twice. It fetched on every mount and revoked on
 * every unmount until the job page (#115) showed what that meant: every
 * open of a call moved the file again. That page reads its thread's
 * recordings as the thread is drawn, so a call opens with its player
 * there; this asks for the file itself only where nothing has yet.
 */
export function Recording({
  id,
  playback,
}: {
  id: string;
  playback?: Playback;
}) {
  const [src, setSrc] = useState<string | null>(() => heldRecordingSrc(id));
  const [bars, setBars] = useState<number[]>(
    () => heldRecordingBars(id) ?? [],
  );
  const [seconds, setSeconds] = useState<number>(() =>
    heldRecordingSeconds(id),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    recordingSrc(id)
      .then((url) => {
        if (!live) return;
        setSrc(url);
        setError(null);
      })
      .catch((err) => {
        if (live) setError(errorMessage(err));
      });
    // Already decoded is already drawn; this fills in only what was not.
    if (heldRecordingBars(id) === null) {
      recordingWaveform(id)
        .then((decoded) => {
          if (!live) return;
          if (decoded.bars.length > 0) setBars(decoded.bars);
          if (decoded.seconds > 0) setSeconds(decoded.seconds);
        })
        .catch(() => {
          // The player is drawn flat; the src's own failure is what is said.
        });
    }
    return () => {
      live = false;
    };
  }, [id]);

  if (error) {
    return (
      <InlineBanner variant="warning" placement="none">
        {error}
      </InlineBanner>
    );
  }
  if (!src) {
    return <p className={styles.loading}>Loading the recording…</p>;
  }
  return (
    <Player src={src} bars={bars} seconds={seconds} playback={playback} />
  );
}

/**
 * The player, built out of the same pieces every other screen is.
 *
 * `<audio controls>` draws the browser's own control bar: a filled
 * circular play button, its own type scale, a hover state on everything,
 * and a kebab hiding Download and Playback speed. None of it can be
 * reached from a stylesheet, because it is the browser's shadow DOM — so
 * on a page with no shadows, no hover states and one accent colour it
 * reads as somebody else's control pasted in. The element stays, since it
 * is what decodes and plays the audio; what is drawn is ours.
 *
 * Four controls and no box: play, back ten seconds, the position, and
 * how far through it is. Skip-back was the kebab's and is the one a
 * person checking a call against its transcript actually reaches for — a
 * caller says a number once. Speed is not offered: a recording played
 * fast is one the reader has to go back over, and this is a page for
 * reading a call rather than getting through it. Download stays gone
 * too: the recording is the Company's and it is already theirs, and a
 * file on somebody's desktop is a call recording outside the Company's
 * own system.
 *
 * The position is drawn as the recording's own loudness, and is still a
 * native range input underneath, which is the house treatment for a
 * slider (DESIGN.md) and is what keeps it reachable from the keyboard.
 *
 * Its length is the decoded one (`seconds`) until the `<audio>` has read
 * the file's header, so the clock reads the call's length on the first
 * paint rather than 0:00 / 0:00 until the element catches up.
 */
export function Player({
  src,
  bars,
  seconds = 0,
  playback,
  counted = true,
}: {
  src: string;
  bars: number[];
  /** The recording's length as decoded, before the element measures it. */
  seconds?: number;
  playback?: Playback;
  /**
   * Whether a play is a **Call Recording** listened to. Off for a
   * number's recorded greeting (Settings → Phone), which is the same
   * player and not a call.
   */
  counted?: boolean;
}) {
  const audio = useRef<HTMLAudioElement>(null);
  const fill = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  // What the element measured, once it has. A blob it cannot measure
  // reports Infinity, and the decoded length stands in for it.
  const [measured, setMeasured] = useState(0);
  const length = measured > 0 ? measured : seconds;
  // Counted once per recording opened: a pause and a skip down the
  // Transcript are the same listen.
  const played = useRef(false);

  // The position, told to whoever is following it (#118): the same
  // reading the clock gets, at the same rate.
  const onPosition = playback?.onPosition;
  useEffect(() => onPosition?.(at, playing), [onPosition, at, playing]);

  // And a seek taken from there: a turn pressed on the Transcript moves
  // the recording to where it starts and plays it, which is what a
  // person pressing a line wants to hear.
  const seekRef = playback?.seekRef;
  useEffect(() => {
    if (!seekRef) return;
    seekRef.current = (secs: number) => {
      const el = audio.current;
      if (!el) return;
      el.currentTime = secs;
      setAt(secs);
      void el.play();
    };
    return () => {
      seekRef.current = null;
    };
  }, [seekRef]);

  // A blob the browser cannot measure reports its duration as Infinity.
  // Where nothing decoded a length either, the scrubber is drawn empty
  // and disabled rather than at a position that means nothing.
  const seekable = length > 0;

  /**
   * Where the played bars end, written straight onto the node.
   *
   * Not through React: the position is one style property, and putting
   * it through a render would reconcile every bar in the row to move a
   * boundary.
   */
  const paint = useCallback((secs: number, total: number) => {
    const node = fill.current;
    if (!node) return;
    const done = total > 0 ? Math.min(100, (secs / total) * 100) : 0;
    node.style.clipPath = `inset(0 ${100 - done}% 0 0)`;
  }, []);

  // Before the browser paints, so the row is never briefly green to the
  // end. This is the position while the call is paused, seeking, or
  // finished.
  useLayoutEffect(() => paint(at, length), [at, length, paint]);

  // And every frame while it plays. `timeupdate` fires about four times
  // a second, which is plenty for a clock reading m:ss and is visibly
  // stepped on a boundary crossing the screen. Nothing here eases or
  // transitions — it is the same true position, read as often as it is
  // drawn.
  useEffect(() => {
    if (!playing) return;
    let frame = requestAnimationFrame(function step() {
      if (audio.current) paint(audio.current.currentTime, length);
      frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [playing, length, paint]);

  return (
    <div className={styles.player}>
      {/* Not drawn (`.audio`): an element with no `controls` has no box
          of its own in some browsers and a stray one in others, and it
          plays either way. What is drawn is the row around it. */}
      <audio
        ref={audio}
        src={src}
        preload="metadata"
        className={styles.audio}
        onLoadedMetadata={(e) =>
          setMeasured(
            Number.isFinite(e.currentTarget.duration)
              ? e.currentTarget.duration
              : 0,
          )
        }
        onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)}
        onPlay={() => {
          setPlaying(true);
          if (counted && !played.current) {
            played.current = true;
            track("recording_played");
          }
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />

      <button
        type="button"
        aria-label={playing ? "Pause" : "Play"}
        className={styles.control}
        onClick={() => {
          const el = audio.current;
          if (!el) return;
          if (el.paused) void el.play();
          else el.pause();
        }}
      >
        {playing ? (
          <PauseIcon className={styles.controlIcon} />
        ) : (
          <PlayIcon className={styles.controlIcon} />
        )}
      </button>

      {/* Ten seconds, which is about one turn: the thing a reader missed
          is nearly always the sentence they were just told. */}
      <button
        type="button"
        aria-label="Back ten seconds"
        disabled={!seekable}
        className={styles.control}
        onClick={() => {
          const el = audio.current;
          if (!el) return;
          const next = Math.max(0, el.currentTime - 10);
          el.currentTime = next;
          setAt(next);
        }}
      >
        <RotateCcwIcon className={styles.backIcon} />
      </button>

      <div
        className={`${styles.waveform} ${seekable ? "" : styles.unseekable}`}
      >
        <Bars bars={bars} className={styles.barsRest} />
        {/* The played part is the same row again, cut off at the
            position: one style property moves per frame rather than a
            hundred and sixty bars being redrawn. */}
        <div ref={fill} className={styles.fill}>
          <Bars bars={bars} className={styles.barsPlayed} />
        </div>

        {/* Over the bars and invisible, because the bars are the track
            and the boundary between the two colours is the thumb. It is
            still the control: it takes the click, the drag, the arrow
            keys and the screen reader. */}
        <input
          type="range"
          aria-label="Position in the recording"
          min={0}
          max={seekable ? length : 0}
          step="any"
          value={at}
          disabled={!seekable}
          onChange={(e) => {
            const next = Number(e.target.value);
            setAt(next);
            if (audio.current) audio.current.currentTime = next;
          }}
          className={styles.waveformInput}
        />
      </div>

      <span className={styles.clock}>
        {clock(at)} / {clock(length)}
      </span>
    </div>
  );
}

/**
 * One row of bars, drawn to whatever width it is given.
 *
 * A recording whose bars could not be counted is drawn flat rather than
 * absent: the row is still the scrubber, and a player that appears only
 * on the codecs a browser will decode is a player that comes and goes.
 */
function Bars({ bars, className }: { bars: number[]; className: string }) {
  const drawn = bars.length > 0 ? bars : FLAT;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${drawn.length * 3} 32`}
      preserveAspectRatio="none"
      className={`${styles.bars} ${className}`}
    >
      {drawn.map((bar, i) => {
        // Never nothing: silence is a line through the middle, so the
        // row reads as a recording that is quiet here rather than as a
        // gap in the control.
        const height = Math.max(2, bar * 32);
        return (
          <rect
            key={i}
            x={i * 3}
            y={(32 - height) / 2}
            width={2}
            height={height}
            rx={1}
            fill="currentColor"
          />
        );
      })}
    </svg>
  );
}

const FLAT: number[] = new Array(BAR_COUNT).fill(0);

/** m:ss, and 0:00 for a length the browser could not read. */
function clock(secs: number): string {
  if (!Number.isFinite(secs) || secs <= 0) return "0:00";
  const whole = Math.floor(secs);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
